import React, { useState } from "react";
import { Send, RotateCcw, Search } from "lucide-react";
import { Button } from "@/components";
import { useApp } from "@/context/AppContext";
import { useAppConfig } from "@/hooks/useAppConfig";
import { chatService } from "@/services/ChatService";
import "./ChatInput.css";

interface ChatInputProps {
  pageRoute?: string;
}

const ChatInput: React.FC<ChatInputProps> = ({ pageRoute = "home" }) => {
  const [query, setQuery] = useState("");
  const { getPageConfig } = useAppConfig();
  const {
    vehicleData,
    messages,
    isLoading,
    addMessage,
    clearMessages,
    setLoading,
  } = useApp();

  // Get page-specific configuration
  const pageConfig = getPageConfig(pageRoute);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!query.trim()) return;

    addMessage({
      type: "user",
      content: query,
      vehicleData,
    });

    setLoading(true);

    try {
      const response = await chatService.searchParts(vehicleData, query);

      if (response.success) {
        if (response.responseType === "markdown") {
          const responseMessage = chatService.generateChatResponse(
            [],
            [],
            query,
            undefined,
            response.markdownContent,
            "markdown"
          );

          addMessage({
            type: "assistant",
            content: responseMessage.content,
            responseType: responseMessage.responseType,
            markdownContent: responseMessage.markdownContent,
          });
        } else if (response.parts || response.vmrsParts) {
          const responseMessage = chatService.generateChatResponse(
            response.parts || [],
            response.vmrsParts || [],
            query,
            response.pt,
            undefined,
            "json"
          );

          addMessage({
            type: "assistant",
            content: responseMessage.content,
            pcdbParts: responseMessage.pcdbParts,
            vmrsParts: responseMessage.vmrsParts,
            pt: response.pt,
            responseType: responseMessage.responseType,
          });
        } else {
          addMessage({
            type: "assistant",
            content:
              "Sorry, I couldn't find any parts for your search. Please try again with different terms.",
          });
        }
      } else {
        addMessage({
          type: "assistant",
          content:
            response.error ||
            "Sorry, I couldn't find any parts for your search. Please try again with different terms.",
        });
      }
    } catch (error) {
      console.error("Search error:", error);
      addMessage({
        type: "assistant",
        content:
          "Sorry, there was an error processing your request. Please try again.",
      });
    } finally {
      setLoading(false);
      setQuery("");
    }
  };

  const handleClearChat = () => {
    clearMessages();
  };

  return (
    <div className="chat-input-container-modern">
      <div className="chat-input-wrapper-modern">
        <form onSubmit={handleSubmit} className="chat-input-form-modern">
          <div className="input-section-modern">
            <div className="input-icon-modern">
              <Search size={20} />
            </div>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                pageConfig?.searchPlaceholder ||
                "Search for parts... (e.g., brake pads, oil filter)"
              }
              className="chat-input-modern"
              disabled={isLoading}
            />
          </div>
          <div className="actions-section-modern">
            {messages.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClearChat}
                className="clear-button-modern"
                disabled={isLoading}
              >
                <RotateCcw size={16} />
                <span className="button-text">Clear</span>
              </Button>
            )}
            <Button
              type="submit"
              size="sm"
              disabled={!query.trim() || isLoading}
              className="send-button-modern"
            >
              <Send size={16} />
              <span className="button-text">Search</span>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatInput;
